/**
 * Unified operations intelligence — nozzle queue proxy, employee tiers, best pairings.
 */
import { useState } from 'react';
import Loader from '../Employee/Loader.component';
import { fetchAdminPumpAnalytics } from '../Admin/adminApi';
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
} from '../../../utils/reportFormat';

const formatPercent = (value) => `${formatNumber(value, 2)}%`;

const priorityClass = (priority) => {
  const map = { critical: 'critical', high: 'warning', medium: 'info', positive: 'info' };
  return map[priority] || 'info';
};

const queueClass = (label) => {
  if (label?.includes('High')) return 'owner-ops-queue--high';
  if (label?.includes('Under')) return 'owner-ops-queue--low';
  return 'owner-ops-queue--ok';
};

export default function OwnerOperationsIntelligencePanel({
  pumpId,
  syncDateRange = null,
}) {
  const [loading, setLoading] = useState(false);
  const [ops, setOps] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const canLoad = Boolean(pumpId && syncDateRange?.from_date && syncDateRange?.to_date);

  const load = () => {
    if (!canLoad || loading) return;

    setLoading(true);
    setError('');
    fetchAdminPumpAnalytics(pumpId, syncDateRange.from_date, syncDateRange.to_date)
      .then((data) => {
        if (data?.status === false) {
          throw new Error(data.message || 'Unable to load operations intelligence');
        }
        setOps(data?.deep_analysis?.operations_intelligence || null);
        setLoaded(true);
      })
      .catch((err) => {
        setOps(null);
        setError(err.message || 'Unable to load operations intelligence');
      })
      .finally(() => setLoading(false));
  };

  if (!canLoad) {
    return (
      <div className="admin-panel">
        <p className="text-muted mb-0">Choose a report period above before loading operations intelligence.</p>
      </div>
    );
  }

  if (!loaded && !loading) {
    return (
      <div className="admin-panel text-center py-5">
        <p className="text-muted mb-3">
          Load operations intelligence for <strong>{syncDateRange.from_date}</strong> → <strong>{syncDateRange.to_date}</strong>.
          This runs a full analytics pass and may take a minute on large pumps.
        </p>
        <button type="button" className="btn btn-primary" onClick={load}>
          Load operations intelligence
        </button>
        {error ? <p className="small text-danger mt-3 mb-0">{error}</p> : null}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Loader />
        <p className="small text-muted mt-2 mb-0">Analysing nozzle and employee operations…</p>
      </div>
    );
  }

  if (!ops?.summary) {
    return (
      <div className="admin-panel">
        <p className="text-muted mb-3">No nozzle closing data for this date range — run operations after employee nozzle closings.</p>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={load}>
          Retry load
        </button>
      </div>
    );
  }

  const summary = ops.summary;
  const tiers = ops.employee_tiers || {};

  return (
    <div className="owner-operations-intelligence">
      <div className="admin-panel mb-3 d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h5 className="mb-1">Operations intelligence</h5>
          <p className="small text-muted mb-2">{ops.headline}</p>
          {ops.data_note ? (
            <p className="small text-muted mb-0"><strong>How we calculate:</strong> {ops.data_note}</p>
          ) : null}
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="owner-wc-kpi">
            <span className="owner-wc-kpi__label">Fair share / nozzle</span>
            <span className="owner-wc-kpi__value">{formatPercent(summary.fair_share_pct)}</span>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="owner-wc-kpi">
            <span className="owner-wc-kpi__label">Avg throughput</span>
            <span className="owner-wc-kpi__value">{formatNumber(summary.avg_litres_per_hour, 1)} L/hr</span>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="owner-wc-kpi">
            <span className="owner-wc-kpi__label">Queue hotspots</span>
            <span className="owner-wc-kpi__value text-danger">{summary.queue_hotspot_count ?? 0}</span>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="owner-wc-kpi">
            <span className="owner-wc-kpi__label">Team avg sales</span>
            <span className="owner-wc-kpi__value">{formatCurrency(summary.team_avg_sales_per_hour)}/hr</span>
          </div>
        </div>
      </div>

      {(ops.playbook || []).length > 0 && (
        <div className="admin-panel mb-4">
          <h6 className="mb-2">Action playbook</h6>
          <div className="owner-ops-playbook-list">
            {ops.playbook.map((item, idx) => (
              <div key={`${item.title}-${idx}`} className={`owner-ops-playbook-item owner-ops-playbook-item--${priorityClass(item.priority)}`}>
                <strong>{item.title}</strong>
                <p className="small mb-1">{item.action}</p>
                {item.why ? <p className="small text-muted mb-0">{item.why}</p> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <div className="dash-card owner-chart-card h-100">
            <h6 className="mb-2">Nozzle queue pressure (L/hr proxy)</h6>
            <div className="table-responsive">
              <table className="table table-sm owner-analytics-table mb-0">
                <thead>
                  <tr>
                    <th>Nozzle</th>
                    <th>Fuel</th>
                    <th className="text-end">Share</th>
                    <th className="text-end">L/hr</th>
                    <th className="text-end">vs fair</th>
                    <th>Load</th>
                  </tr>
                </thead>
                <tbody>
                  {(ops.nozzles || []).slice(0, 12).map((row) => (
                    <tr key={row.nozzle_id || row.name}>
                      <td><strong>{row.name}</strong></td>
                      <td>{row.fuel}</td>
                      <td className="text-end">{formatPercent(row.share_pct)}</td>
                      <td className="text-end">{formatNumber(row.litres_per_hour, 1)}</td>
                      <td className="text-end">{formatNumber(row.share_vs_fair, 2)}×</td>
                      <td>
                        <span className={`owner-ops-queue-badge ${queueClass(row.queue_label)}`}>
                          {row.queue_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="dash-card owner-chart-card h-100">
            <h6 className="mb-2">Employee tiers</h6>
            {['star', 'solid', 'coaching', 'risk'].map((tier) => {
              const rows = tiers[tier] || [];
              if (rows.length === 0) return null;
              return (
                <div className="owner-analytics-tier mb-3" key={tier}>
                  <h6 className={`owner-analytics-tier__label owner-analytics-tier__label--${tier}`}>
                    {tier} ({rows.length})
                  </h6>
                  {rows.slice(0, 4).map((emp) => (
                    <div className="owner-analytics-tier__row" key={emp.emp_id || emp.name}>
                      <span>{emp.name}</span>
                      <span className="small text-muted">₹{formatNumber(emp.sales_per_hour, 0)}/hr</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="dash-card owner-chart-card h-100">
            <h6 className="mb-2">Best employee × nozzle pairs</h6>
            <div className="table-responsive">
              <table className="table table-sm owner-analytics-table mb-0">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Nozzle</th>
                    <th className="text-end">L/hr</th>
                    <th className="text-end">Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {(ops.best_pairs || []).map((row, i) => (
                    <tr key={`${row.employee}-${row.nozzle}-${i}`}>
                      <td>{row.employee}</td>
                      <td>{row.nozzle}</td>
                      <td className="text-end">{formatNumber(row.litres_per_hour, 1)}</td>
                      <td className="text-end">{formatCurrency(row.sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="dash-card owner-chart-card h-100">
            <h6 className="mb-2">Pairs needing coaching</h6>
            {(ops.coaching_pairs || []).length === 0 ? (
              <p className="small text-muted mb-0">No weak pairings flagged — team pairings look balanced.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm owner-analytics-table mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Nozzle</th>
                      <th className="text-end">L/hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ops.coaching_pairs.map((row, i) => (
                      <tr key={`coach-${row.employee}-${row.nozzle}-${i}`}>
                        <td>{row.employee}</td>
                        <td>{row.nozzle}</td>
                        <td className="text-end">{formatNumber(row.litres_per_hour, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
